import React, {Fragment, useCallback, useState} from "react";
import {compose} from "redux";
import {connect} from "react-redux";
import {translate} from "react-i18next";
import {Button, Col, Icon, Modal, Row} from "antd";
import {DataLanguageConsumer} from "../../../contexts/DataLanguage";
import InfiniteScrollTable from "../../../components/infinite-scroll-table";
import {
    GUTTER_SM,
    MODAL_WIDTH_XL,
    TABLE_COL_MIN_WIDTH_ID,
    TABLE_COL_MIN_WIDTH_NAME
} from "../../../styles/constants";
import {
    BUTTON_ADD_AGENCY_TO_CONFIG,
    BUTTON_CUSTOM_ANNOTATIONS, BUTTON_DATA_EXPLORER,
    BUTTON_DELETE,
    BUTTON_DETAIL,
    BUTTON_DOWNLOAD,
    BUTTON_INFO,
    BUTTON_LAYOUT_ANNOTATIONS,
    BUTTON_PREVIEW,
    BUTTON_PUBLISH
} from "../../../styles/buttons";
import {getStringFromArtefactTriplet} from "../../../utils/sdmxJson";
import {countCustomAnnotations, countLayoutAnnotations} from "../../../utils/annotations";
import {PATH_IMPORT_STRUCTURES} from "../../../constants/paths";
import EnhancedModal from "../../../components/enhanced-modal";
import {
    getDataflowPublishingHeaderInfo,
    getDataflowPublishingInfo,
    showDataflowPublishingListDataflowProductionModal,
    showDataflowPublishingListDownloadModal
} from "./actions";
import MappingSetTree from "../mapping-set/mapping-set-tree";
import ModalPreview from "../mapping-set/mapping-set-wizard/components/ModalPreview";
import {
    clearMappingPreviewMappingSet,
    getMappingSetPreviewToken
} from "../mapping-set/mapping-set-wizard/actions";
import {openNewTab} from "../../../middlewares/newTab/actions";
import DataflowInfo from "./DataflowInfo";
import DataflowHeader from "./DataflowHeader";

const mapStateToProps = state => {
    const endpoints = state.config.nodes;
    const endpointId = state.app.endpointId;
    const annotationsConfig = !endpointId ? {} : (endpoints || []).find(endpoint => endpoint.general.id === endpointId).annotations;
    const customAnnotationsConfig = !endpointId ? [] : (endpoints || []).filter(endpoint => endpoint.general.id === endpointId)[0].annotationTabs.tabs;
    return {
        username: state.app.user.username,
        permissions: state.app.user.permissions,
        selectedEndpoint: (endpoints || []).find(endpoint => endpoint.general.id === endpointId),
        appLanguage: state.app.language,
        annotationsConfig,
        customAnnotationsConfig,
        mappingSetPreview: state.scenes.mappingHandler.mappingSet.wizard.mappingSetPreview,
        mappingSetPreviewToken: state.scenes.mappingHandler.mappingSet.wizard.mappingSetPreviewToken, 
    };
};

const mapDispatchToProps = dispatch => ({
    onDataflowProductionModalShow: (dataflowTriplet) => dispatch(showDataflowPublishingListDataflowProductionModal(dataflowTriplet)),
    clearPreview: (mappingSetPreviewToken) => dispatch(clearMappingPreviewMappingSet(mappingSetPreviewToken)),
    getMappingToken: (dataflowTriplet) => dispatch(getMappingSetPreviewToken(dataflowTriplet.id, dataflowTriplet.agencyID, dataflowTriplet.version, null, 1)),
    onOpenNewTab: url => dispatch(openNewTab(url)),
    onDownloadShow: (dataflowTriplet, dsdTriplet) => dispatch(showDataflowPublishingListDownloadModal(dataflowTriplet, dsdTriplet)),
    getDataflowInfo: (dataflowTriplet) => dispatch(getDataflowPublishingInfo(dataflowTriplet)),
    getDataflowHeaderInfo: (dataflowTriplet) => dispatch(getDataflowPublishingHeaderInfo(dataflowTriplet)),
});

const CustomArtefactList = ({
                                t,
                                username,
                                permissions,
                                artefacts,
                                selectedEndpoint,
                                appLanguage,
                                getIsDisabledRow,
                                onCreate,
                                onDetailShow,
                                onDownloadShow,
                                onDelete,
                                onRowClick,
                                onAnnotationsShow,
                                onLayoutAnnotationsShow,
                                showImportButton,
                                onDataflowProductionModalShow,
                                annotationsConfig,
                                customAnnotationsConfig,
                                clearPreview,
                                getMappingToken,
                                mappingSetPreview,
                                mappingSetPreviewToken,
                                onOpenNewTab,
                                getDataflowInfo,
                                getDataflowHeaderInfo,
                            }) => {
    const [showCategorization, setShowCategorization] = useState(false);
    const [showMappingSetPreview, setShowMappingSetPreview] = useState(null);

    const productionAction = useCallback(({agencyID, annotations}) => {
        const disabled = !annotations ||
            !annotations.find(a => a.type === annotationsConfig.readyForProduction.type)
        return (
            permissions &&
            permissions.agencies.filter(agency => agency === agencyID).length > 0
        )
            ? ({
                ...BUTTON_PUBLISH,
                title: disabled ? t('scenes.dataManager.dataflowBuilder.list.actions.production.error') : t('scenes.dataManager.dataflowBuilder.list.actions.production.title'),
                disabled,
                onClick: ({id, agencyID, version}) =>
                    onDataflowProductionModalShow({
                        id,
                        agencyID,
                        version
                    })
            })
            : null
    }, [annotationsConfig, permissions]);

    const dataBrowserAction = useCallback(({isProduction}) => {
        return ({
            ...BUTTON_DATA_EXPLORER,
            title: t('scenes.dataManager.dataflowBuilder.list.actions.dataBrowserPreview'),
            disabled: !isProduction || !selectedEndpoint.dataBrowser.dataBrowserUrl || !selectedEndpoint.dataBrowser.nodeID,
            onClick: ({id, agencyID, version}) =>
                onOpenNewTab(
                    selectedEndpoint.dataBrowser.dataBrowserUrl +
                    `/#/${appLanguage}/` +
                    selectedEndpoint.dataBrowser.nodeID.toLowerCase() +
                    "/" + encodeURI(agencyID) + "," + encodeURI(id) + "," + encodeURI(version)
                )
        })
    }, [annotationsConfig, permissions]);

    return (
        <Fragment>
            <DataLanguageConsumer>
                {() => {
                    return (
                        <InfiniteScrollTable
                            data={artefacts ? artefacts.sort((a, b) => a.id.localeCompare(b.id)) : null}
                            getRowKey={({urn}) => urn}
                            columns={[
                                {
                                    title: t('data.artefact.id.shortLabel'),
                                    dataIndex: 'id',
                                    minWidth: TABLE_COL_MIN_WIDTH_ID
                                },
                                {
                                    title: t('data.artefact.agencyID.shortLabel'),
                                    dataIndex: 'agencyID',
                                },
                                {
                                    title: t('data.artefact.name.shortLabel'),
                                    dataIndex: 'names',
                                    minWidth: TABLE_COL_MIN_WIDTH_NAME
                                },
                                {
                                    title: t('data.artefact.version.shortLabel'),
                                    dataIndex: 'version',
                                    widthToContent: true,
                                    withValuesFilter: true
                                },
                                {
                                    title: t('data.dataflow.hasTranscoding.shortLabel'),
                                    dataIndex: 'hasTranscoding',
                                    width: 30,
                                    withValuesFilter: true,
                                    render: hasTranscoding => hasTranscoding
                                        ? <Icon type="check"/>
                                        : null,
                                    renderText: hasTranscoding => hasTranscoding
                                        ? t('data.dataflow.hasTranscoding.values.true')
                                        : t('data.dataflow.hasTranscoding.values.false')
                                },
                                {
                                    title: t('data.dataflow.hasContentConstraint.shortLabel'),
                                    dataIndex: 'hasContentConstraints',
                                    width: 30,
                                    withValuesFilter: true,
                                    render: hasContentConstraints => hasContentConstraints
                                        ? <Icon type="check"/>
                                        : null,
                                    renderText: hasContentConstraints => hasContentConstraints
                                        ? t('data.dataflow.hasContentConstraints.values.true')
                                        : t('data.dataflow.hasContentConstraints.values.false')
                                },
                                {
                                    title: t('data.dataflow.isProduction.shortLabel'),
                                    dataIndex: 'isProduction',
                                    width: 30,
                                    withValuesFilter: true,
                                    render: isProduction => isProduction
                                        ? <Icon type="check"/>
                                        : null,
                                    renderText: isProduction => isProduction
                                        ? t('data.dataflow.isProduction.values.true')
                                        : t('data.dataflow.isProduction.values.false')
                                },
                                {
                                    title: t('scenes.mappingHandler.DataflowPublishing.hasHeader.shortLabel'),
                                    dataIndex: 'hasCustomHeader',
                                    width: 30,
                                    withValuesFilter: true,
                                    render: hasCustomHeader => hasCustomHeader
                                        ? <Icon type="check"/>
                                        : null,
                                    renderText: hasCustomHeader => hasCustomHeader
                                        ? t('scenes.mappingHandler.DataflowPublishing.hasHeader.values.true')
                                        : t('scenes.mappingHandler.DataflowPublishing.hasHeader.values.false')
                                }
                            ]}
                            multilangStrDataIndexes={["names"]}
                            actions={[]
                                .concat(productionAction)
                                .concat(onDetailShow
                                    ? ({agencyID}) => ({
                                        ...BUTTON_DETAIL,
                                        title: (permissions && permissions.agencies.find(agency => agency === agencyID))
                                            ? t('commons.actions.detail.title')
                                            : t('commons.actions.detailReadOnly.title'),
                                        onClick: ({id, agencyID, version}) => onDetailShow({id, agencyID, version})
                                    })
                                    : []
                                )
                                .concat({
                                    ...BUTTON_PREVIEW,
                                    title: t('scenes.dataManager.dataflowBuilder.list.actions.preview'),
                                    onClick: ({id, agencyID, version}) => {
                                        getMappingToken({id, agencyID, version});
                                        setShowMappingSetPreview({id, agencyID, version});
                                    }
                                })
                                .concat(dataBrowserAction)
                                .concat({
                                    ...BUTTON_INFO,
                                    title: t('scenes.dataManager.dataflowBuilder.list.actions.info'),
                                    onClick: ({id, agencyID, version}) => {
                                        getDataflowInfo({id, agencyID, version});
                                    }
                                })
                                .concat({
                                    ...BUTTON_ADD_AGENCY_TO_CONFIG,
                                    title: t('scenes.mappingHandler.DataflowPublishing.headerAction'),
                                    onClick: ({id, agencyID, version}) => {
                                        getDataflowHeaderInfo({id, agencyID, version});
                                    }
                                })
                                .concat(onDownloadShow
                                    ? {
                                        ...BUTTON_DOWNLOAD,
                                        title: t('commons.actions.download.title'),
                                        onClick: ({id, agencyID, version, dsdTriplet}) => onDownloadShow({
                                            id,
                                            agencyID,
                                            version,
                                        }, dsdTriplet)
                                    }
                                    : []
                                )
                                // .concat((username && onDelete)
                                //     ? ({
                                //         ...BUTTON_DELETE,
                                //         title: t('commons.actions.delete.title'),
                                //         onClick: ({id, agencyID, version}, deselect) => Modal.confirm({
                                //             title: t('components.artefactList.modals.confirms.delete.title', {
                                //                 triplet: getStringFromArtefactTriplet({id, agencyID, version})
                                //             }),
                                //             onOk() {
                                //                 deselect();
                                //                 onDelete({id, agencyID, version});
                                //             },
                                //             cancelText: t('commons.buttons.cancel.title')
                                //         })
                                //     })
                                //     : []
                                // )
                            }
                            fixedActions={[]
                                .concat(onAnnotationsShow
                                    ? ({id, agencyID, version, annotations}) => {
                                        const customAnnotationsCount = countCustomAnnotations(annotations, customAnnotationsConfig);
                                        return (customAnnotationsCount > 0)
                                            ? {
                                                ...BUTTON_CUSTOM_ANNOTATIONS,
                                                title: t('commons.actions.customAnnotations.title') + ": " + customAnnotationsCount,
                                                onClick: () => onAnnotationsShow(annotations, {
                                                    id,
                                                    agencyID,
                                                    version
                                                })
                                            }
                                            : null;
                                    }
                                    : []
                                )
                                .concat((username && onLayoutAnnotationsShow)
                                    ? ({id, agencyID, version, annotations}) => {
                                        const layoutAnnotationsCount = countLayoutAnnotations(annotations, annotationsConfig);
                                        return (layoutAnnotationsCount > 0)
                                            ? ({
                                                ...BUTTON_LAYOUT_ANNOTATIONS,
                                                title: t('commons.actions.layoutAnnotations.title') + ": " + layoutAnnotationsCount,
                                                onClick: () => onLayoutAnnotationsShow(annotations, {
                                                    id,
                                                    agencyID,
                                                    version,
                                                })
                                            })
                                            : null;
                                    }
                                    : []
                                )
                            }
                            rightActions={
                                <Row type="flex" justify="end" gutter={GUTTER_SM}>
                                    <Col>
                                        <Button onClick={() => setShowCategorization(true)}>
                                            {t('scenes.dataManager.dataflowBuilder.categorisationsShowButton')}
                                        </Button>
                                    </Col>
                                    {((onCreate || showImportButton) && username) ?
                                        (<Fragment>
                                            {permissions && permissions.functionality.filter(func =>
                                                func === PATH_IMPORT_STRUCTURES.substr(PATH_IMPORT_STRUCTURES.lastIndexOf("/") + 1)
                                            ).length > 0 && (
                                                <Col>
                                                    <a href={`./#${PATH_IMPORT_STRUCTURES}`}>
                                                        <Button icon="upload">
                                                            {t('components.artefactList.buttons.import.title')}
                                                        </Button>
                                                    </a>
                                                </Col>
                                            )}
                                            {onCreate && (
                                                <Col>
                                                    <Button type="primary" icon="plus" onClick={onCreate}>
                                                        {t('components.artefactList.buttons.create.title')}
                                                    </Button>
                                                </Col>
                                            )}
                                        </Fragment>) : null}
                                </Row>
                            }
                            onRowClick={onRowClick}
                            getIsDisabledRow={getIsDisabledRow}
                        />
                    )
                }}
            </DataLanguageConsumer>
            <ModalPreview isRowsModalVisible={!!showMappingSetPreview}
                          onRowsModalHide={() => {
                              setShowMappingSetPreview(null);
                              clearPreview(mappingSetPreviewToken);
                          }}
                          rows={mappingSetPreview}
                          token={mappingSetPreviewToken}
                          dataflowTriplet={showMappingSetPreview}
            />
            <EnhancedModal
                visible={showCategorization}
                onCancel={() => setShowCategorization(false)}
                width={MODAL_WIDTH_XL}
                title={t('scenes.mappingHandler.DataflowPublishing.categorizationTitle')}
                footer={<Button
                    onClick={() => setShowCategorization(false)}>{t('commons.buttons.close.title')}</Button>}
            >
                <MappingSetTree showButtons={false} showOnlyCategoriesWithDataflows/>
            </EnhancedModal>
            <DataflowInfo />
            <DataflowHeader artefacts={artefacts} />
        </Fragment>
    )
}

export default compose(
    translate(),
    connect(mapStateToProps, mapDispatchToProps)
)(CustomArtefactList);
